import { useCallback, useRef } from 'react'

import { useSelector } from 'react-redux'

import type { MenuURN } from '@library/api-menu-experience'
import type { RecipeUUID } from '@library/menu-service'

import { useBasket } from 'routes/Menu/domains/basket'
import { getBasketDate, getNumPortions } from 'selectors/basket'

import {
  useRecipeCustomisationPanel,
  type RecipeCustomisationPanelData,
} from './useRecipeCustomisationPanel'
import { useRecipeGridItemOverrides } from './useRecipeGridItemOverrides'

export type RecipeGridCustomisationData = RecipeCustomisationPanelData

type UseRecipeGridCustomisationDataArgs = {
  collectionId?: string
  menuId: string
  menuUrn?: MenuURN
}

export const useRecipeGridCustomisationData = ({
  collectionId,
  menuId,
  menuUrn,
}: UseRecipeGridCustomisationDataArgs) => {
  const closePanelForRecipeRef = useRef<(recipeId: RecipeUUID) => void>(() => undefined)
  const { getNumPortionsForRecipeId, getQuantitiesForRecipeId } = useBasket()
  const deliveryDate = useSelector(getBasketDate)
  const numPortions = useSelector(getNumPortions)
  const contextKey = `${menuId}:${deliveryDate}`
  const handleOverrideRemoved = useCallback((recipeId: RecipeUUID) => {
    closePanelForRecipeRef.current(recipeId)
  }, [])
  const {
    getRecipeGridItem,
    getSelectedRecipeIdentifiers,
    prepareOverride,
    reconcileAfterRemoval: reconcileCustomisedRecipeGridItemAfterRemoval,
    refreshOverride: refreshCustomisedRecipeGridItem,
    selectOverride: selectRecipeGridOption,
  } = useRecipeGridItemOverrides({
    boxNumPortions: numPortions,
    contextKey,
    deliveryDate,
    menuId,
    menuUrn,
    getNumPortionsForRecipeId,
    getQuantitiesForRecipeId,
    onOverrideRemoved: handleOverrideRemoved,
  })

  const {
    activeRecipeId,
    closeRecipeCustomisations,
    closeRecipeCustomisationsForRecipe,
    getRecipeCustomisationData,
    openRecipeCustomisations,
    retryRecipeCustomisation,
    selectRecipeCustomisation,
  } = useRecipeCustomisationPanel({
    boxNumPortions: numPortions,
    collectionId,
    deliveryDate,
    menuId,
    menuUrn,
    getNumPortionsForRecipeId,
    getSelectedRecipeIdentifiers,
    prepareRecipeSelection: prepareOverride,
  })

  closePanelForRecipeRef.current = closeRecipeCustomisationsForRecipe

  return {
    activeRecipeId,
    getRecipeGridItem,
    getRecipeCustomisationData,
    openRecipeCustomisations,
    closeRecipeCustomisations,
    selectRecipeCustomisation,
    retryRecipeCustomisation,
    selectRecipeGridOption,
    refreshCustomisedRecipeGridItem,
    reconcileCustomisedRecipeGridItemAfterRemoval,
  }
}
